import cx from 'clsx';
import * as React from 'react';

import { useWindowSize } from '../../../hooks/useWindowSize';
import { CategoryFinancialWebAttributes } from '../../../types/category/categoryTypes';
import JackpotsWrapper, {
  JackpotsWrapperPropsInterface,
} from '../../jackpots-wrapper/JackpotsWrapper';
import ClosedOverlay from '../ClosedOverlay';
import { MEDIUM_BREAKPOINT } from '../constants';
import GhostTile from '../GhostTile';
import {
  GameClickProps,
  GameTileProps,
  GridSizeType,
  OverlayProps,
} from '../types';
import { sliceGameList } from '../utils';
import { TileSlot } from './TileSlot';

import { getRowStyle, getWidgetBannerStyle } from './stylesHelper';
import styles from './WidgetGrid.module.scss';

export interface WidgetTileGridProps {
  widgetTile?: JackpotsWrapperPropsInterface;
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game?: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game?: GameClickProps;
    path: string;
  }) => void;
  overlay?: OverlayProps;
  gameDetailsInfo: boolean;
  gameInfoTooltip: string;
  type: 'Hero' | 'Banner';
  size?: GridSizeType;
  gamesLimit?: number;
  initialIndex?: number;
  categoryAttributes?: CategoryFinancialWebAttributes;
}

export const WidgetGrid: React.FC<WidgetTileGridProps> = ({
  widgetTile,
  onClick,
  gameList,
  onInfoClick,
  overlay,
  gameDetailsInfo,
  gameInfoTooltip,
  type,
  size,
  gamesLimit = 3,
  initialIndex = 0,
  categoryAttributes,
}) => {
  const windowWidth = useWindowSize().width;

  if (!gameList && !overlay) return null;

  const slicedGameList = sliceGameList(gamesLimit, gameList);
  const commonTileSlotProps = {
    onClick,
    onInfoClick,
    gameDetailsInfo,
    gameInfoTooltip,
    categoryAttributes,
    overlay,
  };

  const isMediumSize =
    (size === undefined && windowWidth >= MEDIUM_BREAKPOINT.min) ||
    size === 'medium';

  return (
    <div className={styles.container} data-testid="jackpot-grid-grid">
      <div className={styles.rowsWrapper}>
        <div
          className={cx(type === 'Banner' && getWidgetBannerStyle(size), {
            [styles.jackpotBannerSlot]: type === 'Banner',
            [styles.jackpotHeroSlot]: type === 'Hero',
          })}
          data-testid="jackpot-slot"
        >
          {overlay ? (
            <GhostTile />
          ) : (
            widgetTile && (
              <JackpotsWrapper
                {...widgetTile}
                size={type === 'Banner' ? 'wide' : 'slim'}
              />
            )
          )}
        </div>

        {(type === 'Hero' || isMediumSize) && (
          <div className={styles.heroRow}>
            {slicedGameList.slice(0, 2).map((game, index) => (
              <TileSlot
                key={index}
                {...commonTileSlotProps}
                gamePosition={index + 1 + initialIndex}
                tileSize="small"
                slot={game}
              />
            ))}
          </div>
        )}

        {isMediumSize && (
          <div className={styles.rowsWrapper}>
            <div className={styles.mediumRow}>
              {
                <TileSlot
                  {...commonTileSlotProps}
                  gamePosition={2}
                  tileSize="large"
                  slot={slicedGameList[2]}
                />
              }
            </div>
            <div className={styles.mediumRow}>
              {
                <TileSlot
                  {...commonTileSlotProps}
                  gamePosition={3}
                  tileSize="small"
                  slot={slicedGameList[3]}
                />
              }
              {
                <TileSlot
                  {...commonTileSlotProps}
                  gamePosition={4}
                  tileSize="small"
                  slot={slicedGameList[4]}
                />
              }
            </div>
          </div>
        )}
      </div>

      <div className={cx(getRowStyle(size))}>
        {slicedGameList
          .slice(type === 'Hero' ? 2 : 0, type === 'Hero' ? 5 : 3)
          .map((game, index) => (
            <TileSlot
              key={index}
              {...commonTileSlotProps}
              gamePosition={
                index + 2 + initialIndex + (type === 'Hero' ? 2 : 0)
              }
              tileSize="small"
              slot={game}
            />
          ))}
      </div>

      {!!overlay && <ClosedOverlay {...overlay} />}
    </div>
  );
};
